import React from 'react';
import { Link } from 'react-router-dom';
import './CommunityImpact.css';
import PageHero from './PageHero';

const IMPACT = [
  {
    title: 'Student mentorship',
    description:
      'Guidance and support for fellow students getting into technology and media creation.',
    stat: '15+',
    statLabel: 'Students mentored',
    details:
      'Weekly coding sessions, project reviews, and career guidance for high school students.',
  },
  {
    title: 'Youth empowerment',
    description:
      'Getting young people into technology and creative careers before anyone tells them they can’t.',
    stat: '100+',
    statLabel: 'Hours volunteered',
    details:
      'Workshops, presentations, and hands-on learning experiences for youth groups.',
  },
];

const INITIATIVES = [
  {
    name: 'Corning Painted Post High School Coding Club',
    description:
      'Teaching students to code and build their own projects, using both hardware and software as the toolkit.',
    status: 'Active',
    impact: '50+ students',
  },
  {
    name: 'Def Init',
    description:
      'A middle-school coding club running the same idea earlier — hardware and software, student-built projects.',
    status: 'Active',
    impact: '25+ participants',
  },
];

const VALUES = [
  {
    title: 'Collaboration',
    body: 'Working with community partners to build things that actually get used.',
  },
  {
    title: 'Education',
    body: 'Sharing what we know so the next group starts further along than we did.',
  },
  {
    title: 'Innovation',
    body: 'Creative approaches to community problems, not recycled playbooks.',
  },
  {
    title: 'Impact',
    body: 'Measuring what we contribute, and pushing that number up.',
  },
];

const INVOLVEMENT = [
  {
    title: 'Volunteer',
    body: 'Share your skills and time with our community programs.',
    cta: 'Contact us',
    to: '/contact?reason=contact',
  },
  {
    title: 'Partner',
    body: 'Collaborate with us on community projects and initiatives.',
    cta: 'Learn more',
    to: '/contact?reason=contact',
  },
  {
    title: 'Support',
    body: 'Help us expand our reach through resources and connections.',
    cta: 'Get started',
    to: '/contact?reason=appointment',
  },
];

const PARTNERSHIP = [
  {
    title: 'Local reputation',
    body: 'Build trust and loyalty in your community through meaningful partnerships.',
  },
  {
    title: 'Networking',
    body: 'Connect with other local businesses and community leaders.',
  },
  {
    title: 'Brand visibility',
    body: 'Increase awareness through genuine community involvement.',
  },
];

const CommunityImpact = () => (
  <>
    <PageHero
      eyebrow="Studio / Community"
      title="Skills are worth more when you hand them on."
      lede="We use what we've learned to open the door for other students in the Corning-Painted Post area — coding clubs, mentorship, and workshops."
      meta={['2 active clubs', '75+ students reached', '100+ volunteer hours']}
    >
      <Link
        to={`/contact?reason=appointment&topic=${encodeURIComponent('Community partnership')}`}
        className="btn btn--primary"
      >
        Partner with us <span className="btn-arrow">→</span>
      </Link>
    </PageHero>

    <main className="main-content" id="main">
      {/* ---- Impact ------------------------------------------------------- */}
      <section className="section impact-section">
        <div className="container">
          <div className="section-head">
            <p className="eyebrow">Our impact</p>
            <h2 className="section-title reveal reveal-up">Where the hours go.</h2>
            <p className="section-lede reveal reveal-up" style={{ '--reveal-delay': '0.08s' }}>
              Numbers we can actually point at, not rounded-up claims.
            </p>
          </div>

          <ul className="impact-grid" data-stagger="0.08">
            {IMPACT.map((item) => (
              <li className="impact-card reveal reveal-up" key={item.title}>
                <span className="impact-stat">{item.stat}</span>
                <span className="impact-stat-label">{item.statLabel}</span>
                <h3 className="impact-title">{item.title}</h3>
                <p className="impact-description">{item.description}</p>
                <p className="impact-details">{item.details}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ---- Initiatives --------------------------------------------------- */}
      <section className="section section--alt initiatives-section">
        <div className="container">
          <div className="section-head">
            <p className="eyebrow">Running now</p>
            <h2 className="section-title reveal reveal-up">Current initiatives.</h2>
          </div>

          <ul className="initiatives-list" data-stagger="0.07">
            {INITIATIVES.map((initiative) => (
              <li className="initiative-card reveal reveal-up" key={initiative.name}>
                <div className="initiative-header">
                  <h3 className="initiative-title">{initiative.name}</h3>
                  <span className={`tag initiative-status ${initiative.status.toLowerCase()}`}>
                    <span className="dot-live" aria-hidden="true" />
                    {initiative.status}
                  </span>
                </div>
                <p className="initiative-description">{initiative.description}</p>
                <span className="initiative-impact">{initiative.impact}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ---- Values -------------------------------------------------------- */}
      <section className="section values-section">
        <div className="container">
          <div className="section-head">
            <p className="eyebrow">What we hold to</p>
            <h2 className="section-title reveal reveal-up">Our values.</h2>
          </div>

          <ul className="values-grid" data-stagger="0.06">
            {VALUES.map((value, idx) => (
              <li className="value-card reveal reveal-up" key={value.title}>
                <span className="value-index">{String(idx + 1).padStart(2, '0')}</span>
                <h3>{value.title}</h3>
                <p>{value.body}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ---- Get involved -------------------------------------------------- */}
      <section className="section section--alt get-involved-section">
        <div className="container">
          <div className="section-head">
            <p className="eyebrow">Join in</p>
            <h2 className="section-title reveal reveal-up">Get involved.</h2>
            <p className="section-lede reveal reveal-up" style={{ '--reveal-delay': '0.08s' }}>
              Three ways to contribute — pick whichever fits what you have to give.
            </p>
          </div>

          <ul className="involvement-grid" data-stagger="0.07">
            {INVOLVEMENT.map((item) => (
              <li className="involvement-card reveal reveal-up" key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
                <Link to={item.to} className="btn btn--link">
                  {item.cta} <span className="btn-arrow">→</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ---- Business partnership ------------------------------------------ */}
      <section className="section partnership-section">
        <div className="container">
          <div className="partnership-grid">
            <div className="partnership-copy">
              <p className="eyebrow">For business owners</p>
              <h2 className="section-title reveal reveal-up">
                Partner with us for community impact.
              </h2>
              <p className="section-lede reveal reveal-up" style={{ '--reveal-delay': '0.08s' }}>
                Want to make a difference locally? Let&rsquo;s work out how we can create
                real change together while building your reputation where it counts.
              </p>
              <div className="btn-row reveal reveal-up" style={{ '--reveal-delay': '0.16s' }}>
                <Link
                  to={`/contact?reason=appointment&topic=${encodeURIComponent('Community partnership')}`}
                  className="btn btn--primary btn--lg"
                >
                  Discuss partnership <span className="btn-arrow">→</span>
                </Link>
              </div>
              <p className="partnership-note">
                Free consultation · Explore mutual benefits · Make a difference together
              </p>
            </div>

            <ul className="partnership-list" data-stagger="0.06">
              {PARTNERSHIP.map((item) => (
                <li className="partnership-item reveal reveal-left" key={item.title}>
                  <h3>{item.title}</h3>
                  <p>{item.body}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </main>
  </>
);

export default CommunityImpact;
